/**
 * Drawing primitives shared by every screen: tracked text, rules, and the
 * chamfered block that the whole interface is built from. No game knowledge
 * lives here.
 */

import {
  FONT_FAMILY,
  TYPE,
  activeShell,
  bevelInset,
  faces,
  onBlock,
  palette,
  weight as clampWeight,
  type Faces,
} from './theme.ts';
import type { HitList } from './hits.ts';

export interface TextOptions {
  size: number;
  color: string;
  /** Letter spacing as a fraction of the font size (0.15 = the UI default). */
  tracking: number;
  align?: 'left' | 'right' | 'center';
  weight?: number;
}

/** A `TYPE` token plus the colour it is drawn in. */
export type TypeToken = { size: number; weight: number; tracking: number };

export interface BlockLabelOptions {
  /** Text pinned to the left edge, vertically centred. */
  label: string;
  labelType?: TypeToken;
  /** Optional second string pinned to the right edge (a key hint). */
  hint?: string;
  hintType?: TypeToken;
}

export class Painter {
  constructor(readonly ctx: CanvasRenderingContext2D) {}

  /**
   * Draw uppercase text with explicit letter tracking. Done per character so
   * spacing is identical everywhere, regardless of `ctx.letterSpacing` support.
   */
  text(value: string, x: number, y: number, opts: TextOptions): number {
    const ctx = this.ctx;
    const chars = [...value.toUpperCase()];
    ctx.font = `${clampWeight(opts.weight ?? 400)} ${opts.size}px ${FONT_FAMILY}`;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';

    const spacing = opts.size * opts.tracking;
    let width = 0;
    const widths = chars.map((ch) => {
      const w = ctx.measureText(ch).width;
      width += w + spacing;
      return w;
    });
    width = Math.max(0, width - spacing);

    let cursor = x;
    if (opts.align === 'right') cursor = x - width;
    else if (opts.align === 'center') cursor = x - width / 2;
    const startX = cursor;

    ctx.fillStyle = opts.color;
    chars.forEach((ch, i) => {
      ctx.fillText(ch, cursor, y);
      cursor += widths[i] + spacing;
    });
    return cursor - startX - spacing;
  }

  /** `text` driven by a `TYPE` token. */
  typed(
    value: string,
    x: number,
    y: number,
    token: TypeToken,
    color: string,
    align?: TextOptions['align'],
  ): number {
    return this.text(value, x, y, {
      size: token.size,
      weight: token.weight,
      tracking: token.tracking,
      color,
      align,
    });
  }

  /** Width the same string would occupy, without drawing it. */
  measure(value: string, opts: TextOptions): number {
    const ctx = this.ctx;
    ctx.font = `${clampWeight(opts.weight ?? 400)} ${opts.size}px ${FONT_FAMILY}`;
    const spacing = opts.size * opts.tracking;
    const chars = [...value.toUpperCase()];
    let width = 0;
    for (const ch of chars) width += ctx.measureText(ch).width + spacing;
    return Math.max(0, width - spacing);
  }

  measureTyped(value: string, token: TypeToken): number {
    return this.measure(value, {
      size: token.size,
      weight: token.weight,
      tracking: token.tracking,
      color: '#000',
    });
  }

  /** Truncate tracked text to a fixed slot, retaining an explicit ellipsis. */
  fit(value: string, maxWidth: number, token: TypeToken): string {
    if (this.measureTyped(value, token) <= maxWidth) return value;
    const ellipsis = '...';
    let fitted = value;
    while (fitted.length > 0 && this.measureTyped(`${fitted}${ellipsis}`, token) > maxWidth) {
      fitted = fitted.slice(0, -1);
    }
    return `${fitted}${ellipsis}`;
  }

  /** Small uppercase section caption (PLAYER, HOLD, NEXT…). */
  label(value: string, x: number, y: number, color: string = palette().textLabel): void {
    this.typed(value, x, y, TYPE.label, color);
  }

  fillRect(x: number, y: number, w: number, h: number, color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(x, y, w, h);
  }

  /** Horizontal rule. 3px in the block language. */
  rule(x: number, y: number, w: number, color: string = palette().rule, h = 3): void {
    this.fillRect(x, y, w, h, color);
  }

  // ------------------------------------------------------------ the primitive

  /**
   * A beveled rectangle: base fill plus four mitred trapezoids. `inset`
   * defaults to 13% of the shorter side — 4px on a 30px board cell, 2px on a
   * 13px preview cell.
   */
  bevelRect(
    x: number,
    y: number,
    w: number,
    h: number,
    f: Faces,
    inset = bevelInset(Math.min(w, h)),
  ): void {
    const ctx = this.ctx;
    const i = inset;
    const r = x + w;
    const b = y + h;

    ctx.fillStyle = f.bg;
    ctx.fillRect(x, y, w, h);

    quad(ctx, [x, y, r, y, r - i, y + i, x + i, y + i], f.top);
    quad(ctx, [x, b, x + i, b - i, r - i, b - i, r, b], f.bottom);
    quad(ctx, [x, y, x + i, y + i, x + i, b - i, x, b], f.left);
    quad(ctx, [r, y, r, b, r - i, b - i, r - i, y + i], f.right);
  }

  /** Square convenience form — a playfield or preview cell. */
  blockCell(x: number, y: number, size: number, colorOrFaces: string | Faces, alpha = 1): void {
    const f = typeof colorOrFaces === 'string' ? faces(colorOrFaces) : colorOrFaces;
    if (alpha >= 1) {
      this.bevelRect(x, y, size, size, f);
      return;
    }
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    this.bevelRect(x, y, size, size, f);
    ctx.restore();
  }

  // --------------------------------------------------------- composed shapes

  /**
   * A block with a label on the left and an optional key hint on the right —
   * the menu actions and the pause menu are all this shape.
   */
  blockButton(
    x: number,
    y: number,
    w: number,
    h: number,
    f: Faces,
    inset: number,
    textColor: string,
    hintColor: string,
    opts: BlockLabelOptions,
  ): void {
    this.bevelRect(x, y, w, h, f, inset);
    const labelType = opts.labelType ?? TYPE.buttonPrimary;
    const baseline = y + h / 2 + labelType.size * 0.36;
    this.typed(opts.label, x + inset + 9, baseline, labelType, textColor);
    if (opts.hint !== undefined) {
      const hintType = opts.hintType ?? TYPE.buttonKey;
      this.typed(opts.hint, x + w - inset - 9, y + h / 2 + hintType.size * 0.36, hintType, hintColor, 'right');
    }
  }

  /**
   * A small colour-coded block with a word in it: status pills, B2B / COMBO,
   * the scoreboard's YOU marker. Returns the width consumed.
   */
  chip(
    value: string,
    x: number,
    y: number,
    color: string,
    token: TypeToken = TYPE.chip,
    padX = 7,
    padY = 3,
    inset = 3,
  ): number {
    return this.chipOn(value, x, y, faces(color), onBlock(color).text, token, padX, padY, inset);
  }

  /** `chip` on an explicit face set — for neutral, shell-coloured pills. */
  chipOn(
    value: string,
    x: number,
    y: number,
    f: Faces,
    textColor: string,
    token: TypeToken = TYPE.chip,
    padX = 7,
    padY = 3,
    inset = 3,
  ): number {
    const w = chipWidth(this, value, token, padX, inset);
    const h = chipHeight(token, padY, inset);
    this.bevelRect(x, y, w, h, f, inset);
    this.typed(value, x + w / 2, y + h / 2 + token.size * 0.36, token, textColor, 'center');
    return w;
  }

  /**
   * A header pill, right-aligned to `rightX` and vertically centred on the
   * brand text's baseline. Pass `faces`/`textColor` for a neutral pill.
   * Returns its bounds so the caller can register a hit region or stack the
   * next pill beside it.
   */
  headerChip(
    value: string,
    rightX: number,
    baselineY: number,
    fill: string | { f: Faces; textColor: string },
  ): { x: number; y: number; w: number; h: number } {
    const token = TYPE.status;
    const padX = 10;
    const padY = 4;
    const inset = 3;
    const w = chipWidth(this, value, token, padX, inset);
    const h = chipHeight(token, padY, inset);
    const x = rightX - w;
    const y = Math.round(baselineY - h / 2 - token.size * 0.36);
    if (typeof fill === 'string') this.chip(value, x, y, fill, token, padX, padY, inset);
    else this.chipOn(value, x, y, fill.f, fill.textColor, token, padX, padY, inset);
    return { x, y, w, h };
  }

  /**
   * A pixel-grid glyph: one `fillRect` per `#`. Kept flat rather than beveled —
   * at this size the mitre stops reading.
   */
  glyph(rows: readonly string[], x: number, y: number, color: string, pixel = 2): void {
    this.ctx.fillStyle = color;
    for (let r = 0; r < rows.length; r++) {
      for (let c = 0; c < rows[r].length; c++) {
        if (rows[r][c] === '#') this.ctx.fillRect(x + c * pixel, y + r * pixel, pixel, pixel);
      }
    }
  }

  /** Rank badge: a palette block with the numeral in it. */
  badge(value: string, x: number, y: number, size: number, color: string): void {
    this.bevelRect(x, y, size, size, faces(color), bevelInset(size));
    const fontSize = Math.round(size * 0.38);
    this.text(value, x + size / 2, y + size / 2 + fontSize * 0.36, {
      size: fontSize,
      weight: 800,
      tracking: 0,
      color: onBlock(color).text,
      align: 'center',
    });
  }
}

/**
 * Violet: the one tetromino hue no status pill uses, so the theme switch never
 * reads as a state badge. Identical in both shells — the icon carries the
 * state, the colour marks the control.
 */
const THEME_CHIP_COLOR = '#8c54d0';
const SOUND_CHIP_COLOR = '#63c9d8';

/** 7x7 glyphs, painted a pixel at a time so they stay as hard-edged as the blocks. */
const MOON_GLYPH = [
  '..###..',
  '.##....',
  '##.....',
  '##.....',
  '##.....',
  '.##....',
  '..###..',
];
const SUN_GLYPH = [
  '...#...',
  '.#...#.',
  '..###..',
  '#.###.#',
  '..###..',
  '.#...#.',
  '...#...',
];
const SOUND_GLYPH = [
  '.......',
  '..#..#.',
  '.##...#',
  '###...#',
  '.##...#',
  '..#..#.',
  '.......',
];
const MUTED_GLYPH = [
  '.......',
  '..#.#..',
  '.###...',
  '###.#..',
  '.###...',
  '..#.#..',
  '.......',
];
/** A quaver: filled head, stem, flag. */
const MUSIC_GLYPH = [
  '....###',
  '....#.#',
  '....###',
  '....#..',
  '.##.#..',
  '####...',
  '.##....',
];
/** The same quaver, struck through. */
const MUSIC_OFF_GLYPH = [
  '#...###',
  '.#..#.#',
  '..#.###',
  '...##..',
  '.##.#..',
  '####.#.',
  '.##...#',
];
const VOLUME_GLYPHS = {
  low: ['.......', '.......', '.......', '.##....', '.##....', '.##....', '.......'],
  medium: ['.......', '.......', '....##.', '.##.##.', '.##.##.', '.##.##.', '.......'],
  high: ['.......', '......#', '....###', '.##.###', '.##.###', '.##.###', '.......'],
} as const;
const GLYPH_PIXEL = 2;
const GLYPH_SIZE = 7 * GLYPH_PIXEL;

/**
 * The Night / Day switch: a violet header pill carrying a moon or sun and the
 * shell it is currently in. It registers its own hit region and returns its
 * width, so callers can stack the next pill beside it.
 */
export function drawThemeChip(
  p: Painter,
  hits: HitList,
  rightX: number,
  baselineY: number,
): number {
  const night = activeShell() === 'night';
  const label = night ? 'NIGHT' : 'DAY';
  const token = TYPE.status;
  const padX = 10;
  const padY = 4;
  const inset = 3;
  const gap = 7;

  const w = Math.round(
    p.measureTyped(label, token) + GLYPH_SIZE + gap + (padX + inset) * 2,
  );
  const h = Math.round(token.size + (padY + inset) * 2);
  const x = rightX - w;
  const y = Math.round(baselineY - h / 2 - token.size * 0.36);
  const ink = onBlock(THEME_CHIP_COLOR).text;

  p.bevelRect(x, y, w, h, faces(THEME_CHIP_COLOR), inset);
  const glyphX = x + inset + padX;
  p.glyph(night ? MOON_GLYPH : SUN_GLYPH, glyphX, Math.round(y + (h - GLYPH_SIZE) / 2), ink);
  p.typed(label, glyphX + GLYPH_SIZE + gap, y + h / 2 + token.size * 0.36, token, ink);

  hits.add('theme', x, y, w, h);
  return w;
}

/** The persisted audio state, displayed as a speaker or crossed speaker. */
export function drawSoundChip(
  p: Painter,
  hits: HitList,
  rightX: number,
  baselineY: number,
  muted: boolean,
): number {
  const label = muted ? 'MUTED' : 'SOUND';
  const token = TYPE.status;
  const padX = 10;
  const padY = 4;
  const inset = 3;
  const gap = 7;
  const w = Math.round(p.measureTyped(label, token) + GLYPH_SIZE + gap + (padX + inset) * 2);
  const h = Math.round(token.size + (padY + inset) * 2);
  const x = rightX - w;
  const y = Math.round(baselineY - h / 2 - token.size * 0.36);
  const ink = onBlock(SOUND_CHIP_COLOR).text;

  p.bevelRect(x, y, w, h, faces(SOUND_CHIP_COLOR), inset);
  const glyphX = x + inset + padX;
  p.glyph(muted ? MUTED_GLYPH : SOUND_GLYPH, glyphX, Math.round(y + (h - GLYPH_SIZE) / 2), ink);
  p.typed(label, glyphX + GLYPH_SIZE + gap, y + h / 2 + token.size * 0.36, token, ink);
  hits.add('sound', x, y, w, h);
  return w;
}

export type VolumeLevel = keyof typeof VOLUME_GLYPHS;

/** Master-volume preset with a one, two, or three-bar pixel icon. */
export function drawVolumeChip(
  p: Painter,
  hits: HitList,
  rightX: number,
  baselineY: number,
  level: VolumeLevel,
): number {
  const label = level === 'medium' ? 'MED' : level.toUpperCase();
  const token = TYPE.status;
  const padX = 10;
  const padY = 4;
  const inset = 3;
  const gap = 7;
  const w = Math.round(p.measureTyped(label, token) + GLYPH_SIZE + gap + (padX + inset) * 2);
  const h = Math.round(token.size + (padY + inset) * 2);
  const x = rightX - w;
  const y = Math.round(baselineY - h / 2 - token.size * 0.36);
  const ink = onBlock(SOUND_CHIP_COLOR).text;

  p.bevelRect(x, y, w, h, faces(SOUND_CHIP_COLOR), inset);
  const glyphX = x + inset + padX;
  p.glyph(VOLUME_GLYPHS[level], glyphX, Math.round(y + (h - GLYPH_SIZE) / 2), ink);
  p.typed(label, glyphX + GLYPH_SIZE + gap, y + h / 2 + token.size * 0.36, token, ink);
  hits.add('volume', x, y, w, h);
  return w;
}

/** The background-score toggle, labelled the way the sound chip is. */
export function drawMusicChip(
  p: Painter,
  hits: HitList,
  rightX: number,
  baselineY: number,
  on: boolean,
): number {
  // `OFF` rather than `MUTED`: two chips reading MUTED side by side would not
  // say which one is off. The struck note carries the rest of the meaning.
  const label = on ? 'MUSIC' : 'OFF';
  const token = TYPE.status;
  const padX = 10;
  const padY = 4;
  const inset = 3;
  const gap = 7;
  const w = Math.round(p.measureTyped(label, token) + GLYPH_SIZE + gap + (padX + inset) * 2);
  const h = Math.round(token.size + (padY + inset) * 2);
  const x = rightX - w;
  const y = Math.round(baselineY - h / 2 - token.size * 0.36);
  const ink = onBlock(SOUND_CHIP_COLOR).text;

  p.bevelRect(x, y, w, h, faces(SOUND_CHIP_COLOR), inset);
  const glyphX = x + inset + padX;
  p.glyph(on ? MUSIC_GLYPH : MUSIC_OFF_GLYPH, glyphX, Math.round(y + (h - GLYPH_SIZE) / 2), ink);
  p.typed(label, glyphX + GLYPH_SIZE + gap, y + h / 2 + token.size * 0.36, token, ink);
  hits.add('music', x, y, w, h);
  return w;
}

function chipWidth(p: Painter, value: string, token: TypeToken, padX: number, inset: number): number {
  return Math.round(p.measureTyped(value, token) + (padX + inset) * 2);
}

function chipHeight(token: TypeToken, padY: number, inset: number): number {
  return Math.round(token.size + (padY + inset) * 2);
}

function quad(ctx: CanvasRenderingContext2D, pts: readonly number[], color: string): void {
  ctx.beginPath();
  ctx.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i], pts[i + 1]);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}
